import React, { useState, useEffect } from 'react';
import { useCMS } from '../context/CMSContext';
import Meta from '../components/Meta';
import AnimatedSection from '../components/AnimatedSection';

export default function Koeletransport() {
  const { getPage, getGallery, loading } = useCMS();
  const page = getPage('koeletransport');
  const gallery = getGallery(page?.gallery_key || 'koeletransport-galleri');

  const [activeImageIndex, setActiveImageIndex] = useState(null);

  // Handle keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (activeImageIndex === null) return;
      if (e.key === 'Escape') setActiveImageIndex(null);
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeImageIndex, gallery]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-red border-t-transparent" />
      </div>
    );
  }

  const title = page?.title || "Køletransport | Chr. Nielsen & Søn V/Leif Nielsen";
  const metaDescription = page?.meta_description || "Vi har 10 kølevogne og har speciale i temperaturreguleret transport af fødevarer...";
  const headline = page?.headline || "Køletransport";
  const contentHtml = page?.content || "";

  const images = gallery?.images || [];

  const handlePrev = () => {
    if (images.length === 0) return;
    setActiveImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    if (images.length === 0) return;
    setActiveImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  return (
    <>
      <Meta title={title} description={metaDescription} pageTitle="Køletransport" />

      {/* Page Header */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/c3c69980-e6b4-4d46-bdae-26d6f6795887/koeletransport-foedevarer.jpg" 
            alt="Køletransport af fødevarer" 
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/85" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-transparent to-transparent" />
        </div>

        <div className="relative z-10 mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold text-white uppercase tracking-tight">
            {headline}
          </h1>
          <div className="h-1.5 w-24 bg-brand-green mx-auto mt-6" />
          <p className="mt-6 text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Vi råder over en moderne vognpark på 10 vogne og har speciale i temperaturreguleret transport af fødevarer.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="pt-10 lg:pt-24 pb-24 bg-brand-dark">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            
            {/* Left Column: Text Content */}
            <div className="lg:col-span-7 space-y-8 text-left">
              <AnimatedSection direction="left" duration={800}>
                <div className="bg-brand-dark-card border border-brand-dark-border rounded-lg p-8 sm:p-12 shadow-xl">
                  <div 
                    className="prose prose-invert max-w-none text-gray-300 leading-relaxed space-y-6 
                      prose-headings:text-white prose-headings:uppercase prose-headings:tracking-wide prose-headings:font-bold
                      prose-h2:text-lg sm:prose-h2:text-2xl prose-h2:border-l-4 prose-h2:border-brand-red prose-h2:pl-4 prose-h2:mt-8 prose-h2:mb-4
                      prose-strong:text-white prose-strong:font-bold"
                    dangerouslySetInnerHTML={{ __html: contentHtml }}
                  />
                </div>
              </AnimatedSection>

              {/* Certification / Membership */}
              <AnimatedSection direction="up" delay={150} duration={800}>
                <div className="bg-brand-dark-card border-l-4 border-brand-green rounded-r-lg p-6 flex items-center gap-4">
                  <div className="text-3xl text-brand-green">
                    <i className="fa-solid fa-shield-halved" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white uppercase tracking-wider text-sm">Medlem af DTL</h4>
                    <p className="text-xs text-gray-400 mt-1">
                      Chr. Nielsen & Søn er stolt medlem af Dansk Transport og Logistik (DTL), hvilket garanterer ordnede forhold og høj kvalitet.
                    </p>
                  </div>
                </div>
              </AnimatedSection>
            </div>

            {/* Right Column: Quick Info Box */}
            <div className="lg:col-span-5 space-y-8">
              <AnimatedSection direction="right" duration={800}>
                <div className="bg-brand-dark-card border border-brand-dark-border rounded-lg p-8 text-left">
                  <h3 className="text-lg sm:text-xl font-bold text-white uppercase tracking-wide border-b border-brand-dark-border pb-4 mb-6">
                    Vores Vognpark
                  </h3>
                  <ul className="space-y-4">
                    <li className="flex items-center justify-between p-3 bg-brand-dark rounded-md">
                      <span className="text-sm text-gray-400 font-medium">Kølevogne</span>
                      <span className="text-sm font-bold text-brand-green bg-brand-green/10 px-3 py-1 rounded-full">10 vogne</span>
                    </li>
                    <li className="flex items-center justify-between p-3 bg-brand-dark rounded-md">
                      <span className="text-sm text-gray-400 font-medium">Total vognpark</span>
                      <span className="text-sm font-bold text-white bg-white/10 px-3 py-1 rounded-full">10 vogne</span>
                    </li>
                  </ul>

                  <div className="mt-8 pt-6 border-t border-brand-dark-border">
                    <h4 className="font-bold text-white uppercase tracking-wider text-xs mb-3">Kørselsmønster</h4>
                    <p className="text-xs text-gray-400 leading-relaxed">
                      Vi kører på ad hoc basis såvel som kontraktkørsel for en lang række fødevarevirksomheder i Jylland og resten af Danmark.
                    </p>
                  </div>
                </div>
              </AnimatedSection>

              {/* Direct Call Box */}
              <AnimatedSection direction="right" delay={150} duration={800}>
                <div className="bg-brand-red rounded-lg p-8 text-left text-white shadow-lg shadow-brand-red/10 relative overflow-hidden">
                  <div className="absolute -right-8 -bottom-8 text-white/10 text-9xl">
                    <i className="fa-solid fa-truck" />
                  </div>
                  <h3 className="text-xl font-bold uppercase tracking-wide mb-2">Brug for hurtig hjælp?</h3>
                  <p className="text-sm text-red-100 mb-6 leading-relaxed">
                    Vi sidder klar ved telefonen til at koordinere din næste køletransport.
                  </p>
                  <a 
                    href="tel:40535984" 
                    className="inline-flex items-center gap-3 px-6 py-3.5 bg-white hover:bg-gray-100 text-brand-red rounded-md font-bold text-base tracking-wide transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                  >
                    <i className="fa-solid fa-phone" />
                    Ring 40 53 59 84
                  </a>
                </div>
              </AnimatedSection>
            </div>

          </div>

          {/* Dynamic Gallery Section */}
          {images.length > 0 && (
            <div className="mt-24 pt-16 border-t border-brand-dark-border">
              <AnimatedSection direction="up" duration={800}>
                <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
                  <h2 className="text-xs font-bold uppercase tracking-[0.25em] text-brand-green">
                    Galleri
                  </h2>
                  <p className="text-xl sm:text-4xl font-extrabold text-white uppercase tracking-tight">
                    Billeder fra hverdagen
                  </p>
                  <div className="h-1 w-20 bg-brand-green mx-auto mt-4" />
                </div>
              </AnimatedSection>

              {/* Touch-friendly Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                {images.map((img, idx) => (
                  <AnimatedSection 
                    key={idx} 
                    direction="scale" 
                    delay={idx * 100} 
                    duration={600}
                  >
                    <button 
                      onClick={() => setActiveImageIndex(idx)}
                      className="relative w-full aspect-square rounded-lg overflow-hidden border border-brand-dark-border group cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-green"
                    >
                      <img 
                        src={`https://assets.auxodev.dk/cdn-cgi/image/width=400,height=400,fit=cover,quality=80,format=auto/${img.replace('https://assets.auxodev.dk/', '')}`} 
                        alt={`Køletransport galleri ${idx + 1}`} 
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                        <i className="fa-solid fa-magnifying-glass-plus text-white text-2xl" />
                      </div>
                    </button>
                  </AnimatedSection>
                ))}
              </div>
            </div>
          )}

        </div>
      </section>

      {/* Lightbox / Modal */}
      {activeImageIndex !== null && images.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4 sm:p-6">
          {/* Close Button */}
          <button 
            onClick={() => setActiveImageIndex(null)}
            className="absolute top-6 right-6 text-white hover:text-brand-red text-3xl p-2 focus:outline-none transition-colors z-50"
            aria-label="Luk galleri"
          >
            <i className="fa-solid fa-xmark" />
          </button>

          {/* Previous Button */}
          <button 
            onClick={handlePrev}
            className="absolute left-4 sm:left-8 text-white hover:text-brand-green text-3xl sm:text-4xl p-4 focus:outline-none transition-colors z-50"
            aria-label="Forrige billede"
          >
            <i className="fa-solid fa-chevron-left" />
          </button>

          {/* Active Image */}
          <div className="relative max-w-5xl max-h-[80vh] flex items-center justify-center select-none">
            <img 
              src={`https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/${images[activeImageIndex].replace('https://assets.auxodev.dk/', '')}`} 
              alt={`Galleribillede ${activeImageIndex + 1}`} 
              className="max-w-full max-h-[80vh] object-contain rounded-md"
            />
            {/* Image Counter */}
            <div className="absolute bottom-[-40px] left-1/2 transform -translate-x-1/2 text-sm text-gray-400 font-mono">
              {activeImageIndex + 1} / {images.length}
            </div>
          </div>

          {/* Next Button */}
          <button 
            onClick={handleNext}
            className="absolute right-4 sm:right-8 text-white hover:text-brand-green text-3xl sm:text-4xl p-4 focus:outline-none transition-colors z-50"
            aria-label="Næste billede"
          >
            <i className="fa-solid fa-chevron-right" />
          </button>
        </div>
      )}
    </>
  );
}
